import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowUpRight } from "lucide-react";
import { SiteHeader } from "@/components/SiteHeader";
import { LeadSection } from "@/components/LeadSection";
import { SiteFooter } from "@/components/SiteFooter";
import texture from "@/assets/preise-texture.webp";
import textureMobile from "@/assets/preise-texture-mobile.webp";

const SITE = "https://shopify-freelancer.net";
const TITLE = "Shopify Freelancer Preise: Shopaufbau, Theme & CRO";
const DESCRIPTION =
  "Preise für Shopify-Arbeit: Shop-Erstellung zum Festpreis nach Umfang, Theme-Anpassungen für 110 € netto pro Stunde, Conversion-Optimierung ab 3.990 € netto.";

const servicePrices = [
  {
    name: "Shopify Shop-Erstellung",
    price: "Festpreis nach Umfang",
    note: "Aufbau oder Umzug Ihres Shops. Der Umfang wird vorab schriftlich festgelegt, ein Angebot erstelle ich nach der kostenlosen Anfrage.",
    href: "/shopify-shop-erstellen-lassen",
  },
  {
    name: "Theme-Anpassung & Entwicklung",
    price: "110 € netto pro Stunde",
    note: "Anpassungen am Theme, neue Sektionen, Liquid-Entwicklung und kleinere Aufgaben. Abrechnung pro Stunde, mit Aufwandsschätzung im Voraus.",
    href: "/shopify-theme-anpassung",
  },
  {
    name: "Conversion-Optimierung",
    price: "ab 3.990 € netto, Festpreis",
    note: "Analyse und Umbau bestehender Shops. Die drei Programme dazu finden Sie weiter unten.",
    href: "/shopify-conversion-optimierung",
  },
];

const tiers = [
  {
    index: "01",
    name: "Sprint",
    price: "3.990 €",
    duration: "4 Wochen",
    promise: "Die offensichtlichen Umsatzverluste abstellen.",
    forWhom: "Shops ab ca. 20.000 € Monatsumsatz, die schnell Bewegung brauchen.",
    scope: [
      "Conversion-Audit Desktop & Mobile",
      "Produkt- und Kollektionsseiten neu aufgebaut",
      "Warenkorb- und Checkout-Flow",
      "Pagespeed & App-Bereinigung",
      "Sauberes Mess-Setup",
      "4 Wochen Support nach Launch",
    ],
    accent: false,
  },
  {
    index: "02",
    name: "Programm",
    price: "9.900 €",
    duration: "3 Monate",
    promise: "Aus einmaliger Optimierung wird ein Testrhythmus.",
    forWhom:
      "Shops ab ca. 60.000 € Monatsumsatz, bei denen jeder Prozentpunkt fünfstellig wirkt.",
    scope: [
      "Alles aus dem Sprint",
      "A/B-Tests mit Hypothese und Auswertung",
      "Monatlicher Deep-Dive zu Zahlen und Nutzerverhalten",
      "Landingpages für die Top-Kampagnen",
      "E-Mail- und Post-Purchase-Flows",
      "Direkter Draht per WhatsApp",
    ],
    accent: true,
  },
  {
    index: "03",
    name: "Partnerschaft",
    price: "18.900 €",
    duration: "6 Monate",
    promise: "Ich sitze auf eurer Seite des Tisches, nicht gegenüber.",
    forWhom:
      "Shops ab ca. 150.000 € Monatsumsatz mit eigenem Team und mehreren Kanälen.",
    scope: [
      "Alles aus dem Programm",
      "Roadmap für Sortiment, Preisarchitektur und Angebote",
      "Wöchentlicher Test- und Umsetzungstakt",
      "Schulung des internen Teams",
      "Zweitmeinung zu Apps und Tools",
      "Priorität bei kurzfristigen Themen",
    ],
    accent: false,
  },
];

export const Route = createFileRoute("/preise")({
  component: PreisePage,
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: `${SITE}/preise` },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [
      { rel: "canonical", href: `${SITE}/preise` },
      { rel: "alternate", hrefLang: "de", href: "https://shopify-freelancer.net/preise" },
      { rel: "alternate", hrefLang: "en", href: "https://shopify-freelancer.net/en/pricing" },
      { rel: "alternate", hrefLang: "x-default", href: "https://shopify-freelancer.net/preise" },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ItemList",
          name: "Shopify Freelancer Preise",
          itemListElement: [
            {
              "@type": "Offer",
              itemOffered: {
                "@type": "Service",
                name: "Shopify Shop-Erstellung",
                description: "Festpreis nach Umfang, Angebot nach kostenloser Anfrage.",
              },
            },
            {
              "@type": "Offer",
              itemOffered: {
                "@type": "Service",
                name: "Shopify Theme-Anpassung und Entwicklung",
              },
              priceSpecification: {
                "@type": "UnitPriceSpecification",
                price: "110",
                priceCurrency: "EUR",
                unitText: "Stunde",
              },
            },
            {
              "@type": "Offer",
              itemOffered: {
                "@type": "Service",
                name: "Shopify Conversion Optimierung",
              },
              priceSpecification: {
                "@type": "PriceSpecification",
                minPrice: "3990",
                priceCurrency: "EUR",
              },
            },
            ...tiers.map((t) => ({
              "@type": "Offer",
              name: `Conversion-Programm: ${t.name}`,
              price: t.price.replace(/[^\d]/g, ""),
              priceCurrency: "EUR",
              description: t.promise,
            })),
          ],
        }),
      },
    ],
  }),
});

function PreisePage() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <SiteHeader />
      <main>
        {/* Kopf */}
        <section className="relative overflow-hidden border-b border-border/60">
          <picture aria-hidden="true">
            <source media="(max-width: 767px)" srcSet={textureMobile} />
            <img
              src={texture}
              alt=""
              width={1600}
              height={912}
              decoding="async"
              className="pointer-events-none absolute inset-0 h-full w-full object-cover opacity-[0.14] mix-blend-luminosity"
            />
          </picture>
          <div className="relative mx-auto max-w-5xl px-5 py-16 md:py-24">
            <p className="font-mono text-[11px] uppercase tracking-[0.34em] text-primary">
              Honorar / 2026
            </p>
            <h1 className="mt-6 max-w-3xl text-4xl font-bold leading-[1.05] md:text-6xl">
              Preise für <span className="text-primary">Shopify-Arbeit</span>, klar auf den Tisch.
            </h1>
            <p className="mt-7 max-w-xl text-base leading-relaxed text-muted-foreground">
              Shop-Erstellung, Theme-Anpassungen und Conversion-Optimierung: Hier sehen Sie,
              wie ich abrechne, bevor wir miteinander sprechen. Keine versteckten Positionen.
            </p>
            <p className="mt-4 max-w-xl text-sm leading-relaxed text-foreground">
              Sie wählen nur so viel Leistung, wie Ihr Shop wirklich braucht, und kennen den
              Preis, bevor die Arbeit beginnt.
            </p>
          </div>
        </section>

        {/* Preisorientierung alle Leistungen */}
        <section className="border-b border-border/60">
          <div className="mx-auto grid max-w-5xl gap-px overflow-hidden border-x border-border/60 bg-border sm:grid-cols-3">
            {servicePrices.map((s) => (
              <Link
                key={s.name}
                to={s.href}
                className="group bg-background p-7 transition-colors hover:bg-primary/[0.04]"
              >
                <h2 className="text-base font-semibold leading-snug">{s.name}</h2>
                <p className="mt-2 font-display text-2xl font-bold text-primary">{s.price}</p>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{s.note}</p>
                <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-primary group-hover:underline">
                  Details zur Leistung
                  <ArrowUpRight className="h-4 w-4" />
                </span>
              </Link>
            ))}
          </div>
        </section>

        {/* Conversion-Programme */}
        <section className="border-b border-border/60">
          <div className="mx-auto max-w-5xl px-5 pt-16">
            <p className="font-mono text-[11px] uppercase tracking-[0.34em] text-primary">
              Conversion-Programme
            </p>
            <h2 className="mt-5 max-w-2xl text-2xl font-bold md:text-3xl">
              Drei Festpreis-Programme für die Conversion-Optimierung.
            </h2>
            <p className="mt-4 max-w-xl text-sm leading-relaxed text-muted-foreground">
              Keine offenen Stundenlisten, keine Nachträge für den vereinbarten Umfang. Die
              angegebenen Laufzeiten sind Planungswerte, keine Zusage für Testsignifikanz oder
              eine bestimmte Conversion-Steigerung.
            </p>
          </div>
          <div className="mx-auto max-w-5xl px-5">
            {tiers.map((tier) => (
              <article
                key={tier.name}
                className={`grid gap-8 rounded-xl border-b border-border/50 px-5 py-12 last:border-b-0 md:grid-cols-[auto_1fr_auto] md:gap-12 md:px-8 md:py-16 ${
                  tier.accent ? "bg-primary/[0.05]" : ""
                }`}
              >
                <div className="font-mono text-sm text-primary md:pt-2">{tier.index}</div>

                <div className="max-w-xl">
                  <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
                    <h2 className="font-display text-3xl font-bold md:text-4xl">
                      {tier.name}
                    </h2>
                    <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-muted-foreground">
                      {tier.duration}
                    </span>
                  </div>
                  <p className="mt-4 text-lg leading-snug">{tier.promise}</p>
                  <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                    {tier.forWhom}
                  </p>
                  <ul className="mt-7 space-y-2.5 border-l border-border pl-5 text-sm text-muted-foreground">
                    {tier.scope.map((s) => (
                      <li key={s} className="relative">
                        <span className="absolute -left-[21px] top-[0.6em] h-px w-3 bg-primary/60" />
                        {s}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="md:w-56 md:text-right">
                  <div className="font-display text-4xl font-bold tabular-nums md:text-5xl">
                    {tier.price}
                  </div>
                  <p className="mt-1 font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
                    netto, Festpreis
                  </p>
                  <Link
                    to="/kontakt"
                    className={`mt-6 inline-flex items-center gap-2 rounded-full px-5 py-3 text-sm font-semibold transition-transform hover:-translate-y-0.5 ${
                      tier.accent
                        ? "bg-primary text-primary-foreground"
                        : "border border-border text-foreground hover:border-primary"
                    }`}
                  >
                    Kostenlose Einschätzung
                    <ArrowUpRight className="h-4 w-4" />
                  </Link>
                </div>
              </article>
            ))}
          </div>
        </section>

        {/* Wie Sie wählen */}
        <section className="border-b border-border/60 py-16 md:py-20">
          <div className="mx-auto grid max-w-5xl gap-10 px-5 md:grid-cols-[1fr_1.2fr]">
            <div>
              <p className="font-mono text-[11px] uppercase tracking-[0.34em] text-primary">
                Auswahl
              </p>
              <h2 className="mt-5 text-2xl font-bold md:text-3xl">
                Nicht das billigste Paket ist das günstigste.
              </h2>
            </div>
            <div className="space-y-6 text-sm leading-relaxed text-muted-foreground">
              <p>
                <span className="text-foreground">Wie sich der Effekt berechnet:</span>{" "}
                Wie viele zusätzliche Bestellungen eine Optimierung bringt, hängt von Ihrer
                Besucherzahl, der bisherigen Conversion-Rate, dem durchschnittlichen
                Bestellwert und der tatsächlich erreichbaren Verbesserung ab. Es gibt keine
                Garantie für ein bestimmtes Ergebnis. Im Rechner unten spielen Sie das als
                Szenario mit Ihren eigenen Zahlen durch.
              </p>
              <p>
                <span className="text-foreground">Warum drei Optionen?</span> Weil ein
                einzelner Preis nur die Frage „ja oder nein“ zulässt. Drei Optionen machen
                daraus „welche“, und Sie behalten die Kontrolle darüber, wie viel Risiko und
                wie viel Tempo Sie kaufen.
              </p>
              <p>
                <span className="text-foreground">Wenn nichts passt:</span> Dann bauen wir
                ein Paket, das passt. Der Umfang richtet sich nach dem Ergebnis, das Sie
                brauchen, nicht nach einer Liste.
              </p>
              <Link
                to="/roi-rechner"
                className="inline-flex items-center gap-2 text-sm font-semibold text-primary hover:underline"
              >
                Wert im ROI-Rechner durchspielen
                <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </section>

        {/* Rahmen */}
        <section className="border-b border-border/60 py-16">
          <div className="mx-auto max-w-5xl px-5">
            <dl className="grid gap-px overflow-hidden rounded-lg border border-border bg-border sm:grid-cols-3">
              {[
                {
                  t: "Zahlung",
                  d: "50 % bei Start, 50 % bei Übergabe. Bei längeren Paketen monatlich.",
                },
                {
                  t: "Umfang",
                  d: "Vorab schriftlich festgelegt. Änderungen besprechen wir, bevor sie Geld kosten.",
                },
                {
                  t: "Zusammenarbeit",
                  d: "Ein Ansprechpartner: ich. Kein Account Manager, keine Weitergabe an Junioren.",
                },
              ].map((item) => (
                <div key={item.t} className="bg-background p-6">
                  <dt className="font-mono text-[11px] uppercase tracking-[0.22em] text-primary">
                    {item.t}
                  </dt>
                  <dd className="mt-3 text-sm leading-relaxed text-muted-foreground">
                    {item.d}
                  </dd>
                </div>
              ))}
            </dl>
          </div>
        </section>

        <LeadSection source="preise" />
      </main>
      <SiteFooter />
    </div>
  );
}
